(() => {
  const ATLASES = {};
  const PENDING = {};
  const MID_SCALE = 130 / 76;

  const root = document.querySelector("[data-stitch-atlases]");
  const SOURCES = root ? JSON.parse(root.dataset.stitchAtlases) : {};

  function atlasKeyFor(node) {
    const cs = getComputedStyle(node);
    const first = (cs.fontFamily || "")
      .split(",")[0]
      .trim()
      .replace(/^["']|["']$/g, "")
      .toLowerCase();
    if (SOURCES[first]) return first;
    const varName = cs.getPropertyValue("--font-family").trim()
      .replace(/^["']|["']$/g, "").toLowerCase();
    return SOURCES[varName] ? varName : null;
  }

  function threadColour(node) {
    const inline = node.style.color;
    if (inline) return toHex(inline);
    const cs = getComputedStyle(node);
    const v = cs.getPropertyValue("--color").trim();
    return toHex(v || cs.color);
  }

  function load(key) {
    if (ATLASES[key]) return Promise.resolve(ATLASES[key]);
    if (PENDING[key]) return PENDING[key];
    PENDING[key] = fetch(SOURCES[key])
      .then(r => (r.ok ? r.json() : Promise.reject(new Error(r.status))))
      .then(a => { ATLASES[key] = a; return a; })
      .catch(() => null);
    return PENDING[key];
  }

  let _halo = null;
  function haloLayer(target) {
    if (typeof document === "undefined") return null;
    if (!_halo) _halo = document.createElement("canvas");
    if (_halo.width !== target.width || _halo.height !== target.height) {
      _halo.width = target.width;
      _halo.height = target.height;
    }
    return _halo;
  }

  function shade(hex, amt) {
    const n = parseInt(hex.slice(1), 16);
    let r = (n >> 16) & 255, g = (n >> 8) & 255, b = n & 255;
    if (amt >= 0) {
      const t = amt / 170;
      r += (255 - r) * t; g += (255 - g) * t; b += (255 - b) * t;
    } else {
      const t = Math.max(0.25, 1 + amt / 200);
      r *= t; g *= t; b *= t;
    }
    return `rgb(${Math.round(r)},${Math.round(g)},${Math.round(b)})`;
  }

  function toHex(color) {
    if (!color) return "#ffffff";
    if (color.startsWith("#")) return color.length === 4
      ? "#" + [...color.slice(1)].map(c => c + c).join("")
      : color;
    const m = color.match(/\d+/g);
    if (!m) return "#ffffff";
    return "#" + m.slice(0, 3).map(v => (+v).toString(16).padStart(2, "0")).join("");
  }

  const CAPS_ONLY = { varsity: true, "itc-american-typewriter-cond": true };

  function readRuns(node, transform, key) {
    const upper = transform === "uppercase" || CAPS_ONLY[key] === true;
    const lines = [[]];
    const walk = (el, scale) => {
      el.childNodes.forEach(n => {
        if (n.nodeType === Node.TEXT_NODE) {
          const raw = upper ? n.textContent.toUpperCase() : n.textContent;
          [...raw].forEach(ch => {
            const line = lines[lines.length - 1];
            if (/\s/.test(ch)) {
              const last = line[line.length - 1];
              if (line.length && last && last.ch !== " ") line.push({ ch: " ", k: scale });
              return;
            }
            line.push({ ch, k: scale });
          });
        } else if (n.nodeName === "BR") {
          lines.push([]);
        } else if (n.nodeType === Node.ELEMENT_NODE) {
          const bigger = n.classList.contains("product-personal__preview-text-increase");
          walk(n, bigger ? MID_SCALE : scale);
        }
      });
    };
    walk(node, 1);
    lines.forEach(l => { while (l.length && l[l.length - 1].ch === " ") l.pop(); });
    return lines.filter(l => l.length);
  }

  function centreOf(atlas, run) {
    let c = 0, best = -1;
    run.forEach(({ ch, k }) => {
      const g = atlas.glyphs[ch];
      if (g && k > best) { best = k; c = (g.oy + g.h / 2) * k; }
    });
    return c;
  }

  function measure(atlas, run, centred) {
    const K = atlas.kern || {};
    const mid = centred ? centreOf(atlas, run) : 0;
    let total = 0, top = 0, bottom = 0, prev = null;
    run.forEach(({ ch, k }) => {
      const g = atlas.glyphs[ch];
      if (!g) { total += (atlas.glyphs[" "] || {}).adv || atlas.em_px * 0.26; prev = null; return; }
      if (prev) total += (K[prev + ch] || 0) * k;
      prev = ch;
      total += g.adv * k;
      const shift = centred ? mid - (g.oy + g.h / 2) * k : 0;
      top = Math.min(top, g.oy * k + shift);
      bottom = Math.max(bottom, (g.oy + g.h) * k + shift);
    });
    return { total, top, bottom, mid };
  }

  function drawRun(ctx, atlas, run, startX, baseY, S, colour, centred) {
    const K = atlas.kern || {};
    const M = measure(atlas, run, centred);
    let seed = 0;
    let letter = 0;

    const weight = Math.max(0.9, 2.9 * S);
    const jitter = weight * 0.22;
    const lift = weight * 0.45;
    const ragged = weight * 0.15;

    const LX = -0.55, LY = -0.84;

    const pass = (mode, g2d) => {
      const ctx = g2d;
      const halo = mode === "halo";
      const shadow = mode === "shadow" || halo;
      const underlay = mode === "underlay";
      let x = startX, prev = null;
      run.forEach(({ ch, k }) => {
        const g = atlas.glyphs[ch];
        if (!g) { x += (((atlas.glyphs[" "] || {}).adv || atlas.em_px * 0.26)) * S; prev = null; return; }
        if (prev) x += (K[prev + ch] || 0) * k * S;
        prev = ch;
        const drift = Math.sin(letter * 1.7) * 7 + Math.sin(letter * 0.6) * 4;
        letter += 1;
        const shift = centred ? M.mid - (g.oy + g.h / 2) * k : 0;
        const gx = x + g.ox * k * S;
        const gy = baseY + (g.oy * k + shift) * S;
        const gs = k * S;
        const st = g.st;
        const sub = Math.max(1, Math.round(k));
        const limit = (g.adv || atlas.em_px) * 0.25;

        const stride = underlay ? 16 : (halo ? 12 : 4);
        for (let i = 0; i < st.length; i += stride) {
          const has = i + 4 < st.length;
          const gap = has ? Math.hypot(st[i + 4] - st[i], st[i + 5] - st[i + 1]) : Infinity;
          let parallel = true;
          if (has) {
            const d1x = st[i + 2] - st[i], d1y = st[i + 3] - st[i + 1];
            const d2x = st[i + 6] - st[i + 4], d2y = st[i + 7] - st[i + 5];
            const n1 = Math.hypot(d1x, d1y) || 1;
            const n2 = Math.hypot(d2x, d2y) || 1;
            parallel = Math.abs((d1x * d2x + d1y * d2y) / (n1 * n2)) > 0.88;
          }
          const reps = underlay ? 1 : (has && gap < limit && parallel ? sub : 1);
          for (let r = 0; r < reps; r++) {
            const t = reps > 1 ? r / reps : 0;
            const j = shadow ? 0 : ((Math.sin(seed++ * 12.9898) * 43758.5453) % 1) * jitter;
            const p0 = st[i] + (st[i + 4] - st[i]) * t;
            const p1 = st[i + 1] + (st[i + 5] - st[i + 1]) * t;
            const p2 = st[i + 2] + (st[i + 6] - st[i + 2]) * t;
            const p3 = st[i + 3] + (st[i + 7] - st[i + 3]) * t;
            let ax = gx + (reps > 1 ? p0 : st[i]) * gs + j;
            let ay = gy + (reps > 1 ? p1 : st[i + 1]) * gs + j;
            let bx = gx + (reps > 1 ? p2 : st[i + 2]) * gs + j;
            let by = gy + (reps > 1 ? p3 : st[i + 3]) * gs + j;
            if (!halo) {
              const ex = bx - ax, ey = by - ay;
              const el = Math.hypot(ex, ey) || 1;
              const trim = Math.min(underlay ? weight * 1.1 : weight * 0.5, el * 0.3);
              const ra = shadow || underlay ? 0 : (((Math.sin(seed * 31.7) * 43758.5453) % 1)) * ragged;
              const rb = shadow || underlay ? 0 : (((Math.sin(seed * 57.3) * 43758.5453) % 1)) * ragged;
              ax += (ex / el) * (trim - ra); ay += (ey / el) * (trim - ra);
              bx -= (ex / el) * (trim - rb); by -= (ey / el) * (trim - rb);
            }
            if (shadow) {
              const ox = halo ? 0 : lift * 0.7;
              const oy = halo ? 0 : lift;
              ctx.beginPath();
              ctx.moveTo(ax + ox, ay + oy);
              ctx.lineTo(bx + ox, by + oy);
              ctx.stroke();
            } else if (underlay) {
              ctx.beginPath();
              ctx.moveTo(ax, ay);
              ctx.lineTo(bx, by);
              ctx.stroke();
            } else {
              const dx = bx - ax, dy = by - ay;
              const len = Math.hypot(dx, dy) || 1;
              const facing = Math.abs((-dy / len) * LX + (dx / len) * LY);
              const tone = -16 + facing * 58;
              const vary = (((Math.sin(seed * 78.233) * 43758.5453) % 1)) * 14 - 7 + drift;
              const grad = ctx.createLinearGradient(ax, ay, bx, by);
              grad.addColorStop(0, shade(colour, -46 + tone * 0.3 + vary));
              grad.addColorStop(0.38, shade(colour, 22 + tone + vary));
              grad.addColorStop(0.72, shade(colour, -2 + tone * 0.6 + vary));
              grad.addColorStop(1, shade(colour, -54 + tone * 0.3 + vary));
              ctx.strokeStyle = grad;
              ctx.beginPath();
              ctx.moveTo(ax, ay);
              ctx.lineTo(bx, by);
              ctx.stroke();
            }
          }
        }
        x += g.adv * k * S;
      });
    };

    const scratch = haloLayer(ctx.canvas);
    if (scratch) {
      const s2d = scratch.getContext("2d");
      s2d.setTransform(ctx.getTransform());
      s2d.clearRect(0, 0, scratch.width, scratch.height);
      s2d.lineCap = "round";
      s2d.strokeStyle = shade(colour, -150);
      s2d.lineWidth = weight * 2.6;
      pass("halo", s2d);
      ctx.save();
      ctx.globalAlpha = 0.16;
      ctx.filter = `blur(${Math.max(1, weight * 0.9)}px)`;
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.drawImage(scratch, 0, 0);
      ctx.restore();
    }

    ctx.lineCap = "round";
    ctx.save();
    ctx.globalAlpha = 0.26;
    ctx.strokeStyle = shade(colour, -150);
    ctx.lineWidth = weight * 1.05;
    pass("shadow", ctx);
    ctx.restore();

    ctx.save();
    ctx.globalAlpha = 0.55;
    ctx.strokeStyle = shade(colour, -58);
    ctx.lineWidth = weight * 1.5;
    pass("underlay", ctx);
    ctx.restore();

    ctx.lineWidth = weight;
    pass("satin", ctx);
  }

  class StitchPreview {
    constructor(textNode) {
      this.text = textNode;
      this.canvas = document.createElement("canvas");
      this.canvas.className = "product-personal__preview-stitch";
      this.canvas.setAttribute("aria-hidden", "true");
      this.mount();

      this.observer = new MutationObserver(() => this.render());
      this.observer.observe(textNode, {
        childList: true, characterData: true, subtree: true, attributes: true,
        attributeFilter: ["style", "class"]
      });
      this.render();
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(() => this.render());
      }
      [120, 400, 1200].forEach(ms => setTimeout(() => this.render(), ms));
      this.watch();
    }

    watch() {
      const at = [60, 160, 320, 600, 1000, 1600, 2400];
      at.forEach(ms => setTimeout(() => this.render(), ms));
      if (this.beat) clearInterval(this.beat);
      this.beat = setInterval(() => this.render(), 500);
    }

    mount() {
      const host = this.text.offsetParent || this.text.parentElement;
      if (!host) return false;
      if (this.stage !== host) {
        this.stage = host;
        host.appendChild(this.canvas);
        if (window.ResizeObserver) {
          if (this.ro) this.ro.disconnect();
          this.ro = new ResizeObserver(() => this.render());
          this.ro.observe(host);
          this.ro.observe(this.text);
        }
      }
      return true;
    }

    render() {
      if (!this.mount()) return;
      const key = atlasKeyFor(this.text);
      if (!key) return this.fallback();
      load(key).then(atlas => (atlas ? this.paint(atlas, key) : this.fallback()));
    }

    fallback() {
      if (this.stage) this.stage.classList.remove("has-stitch-preview");
      const ctx = this.canvas.getContext("2d");
      if (ctx) ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    }

    paint(atlas, key) {
      const stage = this.stage.getBoundingClientRect();
      const box = this.text.getBoundingClientRect();
      if (!stage.width || !box.width || !box.height) {
        this.last = null;
        const c = this.canvas.getContext("2d");
        if (c) c.clearRect(0, 0, this.canvas.width, this.canvas.height);
        return;
      }

      const sig = [
        Math.round(box.x), Math.round(box.y), Math.round(box.width),
        Math.round(stage.width), Math.round(stage.height),
        this.text.textContent, getComputedStyle(this.text).fontSize,
        this.text.style.fontFamily, this.text.style.color,
        this.text.className
      ].join("|");
      if (sig === this.last) return;
      this.last = sig;

      const dpr = Math.min((window.devicePixelRatio || 1) * 1.5, 3);
      this.canvas.width = Math.round(stage.width * dpr);
      this.canvas.height = Math.round(stage.height * dpr);
      const ctx = this.canvas.getContext("2d");
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, stage.width, stage.height);

      const cs = getComputedStyle(this.text);
      const fontPx = parseFloat(cs.fontSize);
      const colour = threadColour(this.text);
      const runs = readRuns(this.text, cs.textTransform, key);
      if (!runs.length || !fontPx || fontPx < 6) return this.fallback();

      const S0 = fontPx / atlas.em_px;
      let asc = atlas.asc ? atlas.asc * S0 : 0;
      let desc = atlas.desc ? atlas.desc * S0 : 0;
      if (!asc) {
        ctx.font = `${fontPx}px ${cs.fontFamily}`;
        const fm = ctx.measureText("Hg");
        asc = fm.fontBoundingBoxAscent || fontPx * 0.8;
        desc = fm.fontBoundingBoxDescent || fontPx * 0.2;
      }
      const lineH = parseFloat(cs.lineHeight) || fontPx;

      const padRight = parseFloat(cs.paddingRight) || 0;
      const align = cs.textAlign;
      const isMono = this.text.classList.contains("monogram");
      const S = fontPx / atlas.em_px;

      const widths = runs.map(r => measure(atlas, r, isMono).total);
      const widest = Math.max(...widths);

      runs.forEach((run, i) => {
        const M = measure(atlas, run, isMono);
        const lineTop = box.top - stage.top + i * lineH;
        const baseY = lineTop + (lineH - (asc + desc)) / 2 + asc;

        let startX;
        const right = box.right - stage.left - padRight;
        if (align === "center") {
          const centre = box.left - stage.left + box.width / 2;
          startX = centre - (M.total * S) / 2;
        } else {
          startX = right - M.total * S - (widest - M.total) * 0 * S;
        }
        drawRun(ctx, atlas, run, startX, baseY, S, colour, isMono);
      });

      this.stage.classList.add("has-stitch-preview");
    }
  }

  function init() {
    if (!Object.keys(SOURCES).length) return;
    document.querySelectorAll(".product-personal__preview-text").forEach(node => {
      if (node.dataset.stitchBound) return;
      node.dataset.stitchBound = "1";
      new StitchPreview(node);
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
  document.addEventListener("shopify:section:load", init);
  window.addEventListener("orientationchange", () => setTimeout(init, 200));
  document.addEventListener("click", () => setTimeout(init, 300), { passive: true });
})();
